import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { resetPassword } from "../actions/Auth";
import AuthShell from "../ui/AuthShell";

const passwordPattern = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/;

// Sets a new password from the emailed reset link
export default function ResetPassword() {
  const { token } = useParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [postData, setPostData] = React.useState({ password: "", passwordConfirm: "" });
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  async function sendData(e) {
    e.preventDefault();
    if (!postData.password || !postData.passwordConfirm) return setError("Fill in both fields.");
    if (postData.password !== postData.passwordConfirm) return setError("The passwords do not match.");
    if (!passwordPattern.test(postData.password)) {
      return setError(
        "Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol."
      );
    }
    setError("");
    setLoading(true);
    await dispatch(resetPassword(token, postData));
    setLoading(false);
    setSent(true);
    setPostData({ password: "", passwordConfirm: "" });
  }

  const eye = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShow(!show)} edge="end" aria-label={show ? "Hide passwords" : "Show passwords"}>
        {show ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <AuthShell title="Set a new password" subtitle="At least 8 characters, with upper and lower case, a number and a symbol.">
      <Stack component="form" onSubmit={sendData} gap={2.25}>
        {sent && auth.resetError && <Alert severity="error">{auth.resetError}</Alert>}
        {sent && auth.resetSuccess && <Alert severity="success">{auth.resetSuccess}. You can log in now.</Alert>}
        <TextField
          label="New password"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          size="medium"
          value={postData.password}
          onChange={(e) => setPostData({ ...postData, password: e.target.value })}
          InputProps={{ endAdornment: eye }}
        />
        <TextField
          label="Confirm new password"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          size="medium"
          value={postData.passwordConfirm}
          onChange={(e) => setPostData({ ...postData, passwordConfirm: e.target.value })}
          error={Boolean(error)}
          helperText={error}
        />
        <Button type="submit" variant="contained" size="large" disabled={loading}>
          {loading ? "Saving..." : "Save new password"}
        </Button>
        <Link component={RouterLink} to="/insurance/login" variant="body2" underline="hover" sx={{ textAlign: "center" }}>
          Back to log in
        </Link>
      </Stack>
    </AuthShell>
  );
}
